import { capitalize } from "lodash";
import React, { useEffect, useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Snackbar,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from "@mui/icons-material";
import DatasetLinkedOutlinedIcon from "@mui/icons-material/DatasetLinkedOutlined";
import MonitorItemIssuesTable from "./MonitorItemHistoryTable";
import MonitorForm from "./MonitorForm";
import MonitorCreateForm from "./MonitorCreateForm";
import WebsiteStatusResult from "./Results/WebsiteStatusResult";
import Tooltip from "../Tooltip";
import { useTrueBizApi } from "../../api";
import APIPagedMonitors from "../../types/APIPagedMonitors";
import APIMonitor, { APIMonitorType } from "../../types/APIMonitor";
import APIEnquiry from "../../types/APIEnquiry";
import { humanizeDateString } from "../../utilities/formatting";
import { useDebounce } from "../../hooks";

const DEBOUNCE_FILTER_UPDATE_MS = 500;
const RESULTS_PER_PAGE = 15;

export default function MonitorsTable() {
  const api = useTrueBizApi();
  const [searchParams, setSearchParams] = useSearchParams();
  const [textSearch, setTextSearch] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [monitors, setMonitors] = useState<APIPagedMonitors | null>(null);
  const { MonitorsPage: pageString = "1" } = Object.fromEntries(searchParams);
  const [createModalOpen, setCreateModalOpen] = React.useState<boolean>(false);
  const [createNotificationOpen, setCreateNotificationOpen] =
    React.useState<boolean>(false);

  const page = parseInt(pageString, 10);

  const debouncedSearchTerm = useDebounce(
    textSearch,
    DEBOUNCE_FILTER_UPDATE_MS
  );

  const reloadData = useCallback(async () => {
    const offset = (page - 1) * RESULTS_PER_PAGE;

    setLoading(true);
    try {
      setMonitors(
        await api.getMonitors(offset, RESULTS_PER_PAGE, debouncedSearchTerm, [
          APIMonitorType.WebPresence,
          APIMonitorType.WebsiteStatus,
        ])
      );
    } finally {
      setLoading(false);
    }
  }, [api, page, debouncedSearchTerm]);

  useEffect(() => {
    reloadData();
  }, [reloadData]);

  const handleDelete = useCallback(
    async (id: string) => {
      if (
        window.confirm(
          "Are you sure you want to delete this monitor? This action cannot be undone."
        )
      ) {
        await api.deleteMonitor(id);
        reloadData();
      }
    },
    [api, reloadData]
  );

  const setPage = (page: number) => {
    setSearchParams({
      ...Object.fromEntries(searchParams),
      MonitorsPage: String(page),
    });
  };

  return (
    <Paper elevation={2} sx={{ p: 2, mt: 2 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        marginBottom={3}
      >
        <Box>
          <Typography component="h2" fontWeight={700}>
            Monitors
          </Typography>
        </Box>

        <Box display="flex" justifyContent="flex-start" alignItems="left">
          <TextField
            id="outlined-basic"
            label="Search by domain or monitor type"
            variant="outlined"
            size="small"
            sx={{ mr: 1, minWidth: 275 }}
            value={textSearch}
            onChange={(e) => {
              setTextSearch(e.target.value);
            }}
          />

          <Button
            onClick={() => setCreateModalOpen(true)}
            startIcon={<AddIcon />}
            disabled={createModalOpen}
          >
            Add New Monitor
          </Button>
          <MonitorCreateForm
            open={createModalOpen}
            onSubmit={() => {
              setCreateNotificationOpen(true);
              setCreateModalOpen(false);
              reloadData();
            }}
            onClose={() => setCreateModalOpen(false)}
            domain={null}
          />
          <Snackbar
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            open={createNotificationOpen}
            autoHideDuration={5000}
            onClose={() => setCreateNotificationOpen(false)}
            message="Monitor created"
          />
        </Box>
      </Box>

      {loading ? (
        <CircularProgress size={28} sx={{ marginLeft: 1 }} />
      ) : (
        <>
          <TableContainer component={Box} sx={{ marginTop: 3 }}>
            <Table sx={{ minWidth: 650 }} aria-label="Monitors table">
              <TableHead>
                <TableRow>
                  <TableCell />

                  <TableCell>
                    <strong>Domain</strong>
                  </TableCell>

                  <TableCell>
                    <strong>Monitor Type</strong>
                  </TableCell>

                  <TableCell>
                    <strong>Last Monitor Ran</strong>
                  </TableCell>

                  <TableCell>
                    <strong>Next Monitor Runs</strong>
                  </TableCell>

                  <TableCell align="center">
                    <strong>Actions</strong>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {(monitors?.items || []).map(
                  (item: APIMonitor, index: number) => {
                    return (
                      <MonitorRow
                        key={item.id}
                        monitor={item}
                        onUpdate={reloadData}
                        onDelete={handleDelete}
                      />
                    );
                  }
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {monitors && monitors.count > RESULTS_PER_PAGE && (
            <Box
              sx={{ display: "flex", justifyContent: "center", marginTop: 2 }}
            >
              <Pagination
                siblingCount={3}
                count={Math.ceil(monitors.count / RESULTS_PER_PAGE)}
                page={page}
                onChange={(_: React.ChangeEvent<unknown>, value: number) => {
                  setPage(value);
                }}
              />
            </Box>
          )}
        </>
      )}
    </Paper>
  );
}

function MonitorExternalRefId({ externalRefId }: { externalRefId: string }) {
  return (
    <Tooltip
      placement="right"
      title={`External reference id: ${externalRefId}`}
    >
      <DatasetLinkedOutlinedIcon fontSize="small" />
    </Tooltip>
  );
}

function MonitorRow({
  monitor,
  onUpdate,
  onDelete,
}: {
  monitor: APIMonitor;
  onUpdate: () => void;
  onDelete: (id: string) => void;
}) {
  const [drawerOpen, setDrawerOpen] = React.useState<boolean>(false);
  const [editModalOpen, setEditModalOpen] = React.useState<boolean>(false);

  return (
    <React.Fragment key={monitor.id}>
      <TableRow
        sx={{
          "&:last-child td, &:last-child th": { border: 0 },
        }}
      >
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setDrawerOpen(!drawerOpen)}
          >
            {editModalOpen ? (
              <KeyboardArrowUpIcon />
            ) : (
              <KeyboardArrowDownIcon />
            )}
          </IconButton>
        </TableCell>

        <TableCell component="th" scope="row">
          <Box display="flex" gap="0.25em" alignItems="center">
            <Typography>{monitor.domain}</Typography>
            {monitor.external_ref_id && (
              <MonitorExternalRefId externalRefId={monitor.external_ref_id} />
            )}
          </Box>
        </TableCell>

        <TableCell component="th" scope="row">
          <Typography>
            {capitalize(monitor.monitor_type.replaceAll("_", " "))}
          </Typography>
        </TableCell>

        <TableCell component="th" scope="row">
          <Typography>{humanizeDateString(monitor.last_run_at)}</Typography>
        </TableCell>

        <TableCell component="th" scope="row">
          <Typography>{humanizeDateString(monitor.not_before)}</Typography>
        </TableCell>

        <TableCell component="th" scope="row">
          <Box display="flex" justifyContent="center" alignItems="center">
            <IconButton
              onClick={() => {
                setEditModalOpen(true);
              }}
              size="medium"
              color="primary"
            >
              <EditIcon fontSize="inherit" sx={{ margin: 0 }} />
            </IconButton>

            <IconButton
              onClick={() => onDelete(monitor.id)}
              size="medium"
              color="error"
            >
              <DeleteIcon fontSize="inherit" sx={{ margin: 0 }} />
            </IconButton>
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={drawerOpen} timeout="auto" unmountOnExit>
            <LastCompletedEnquiry monitor={monitor} />
            <MonitorItemIssuesTable monitor={monitor} />
          </Collapse>
        </TableCell>
      </TableRow>

      <MonitorForm
        monitor={monitor}
        open={editModalOpen}
        onSubmit={onUpdate}
        onClose={() => {
          setEditModalOpen(false);
        }}
      />
    </React.Fragment>
  );
}

function LastCompletedEnquiry({ monitor }: { monitor: APIMonitor }) {
  const api = useTrueBizApi();
  const [enquiry, setEnquiry] = useState<APIEnquiry | null>(null);

  useEffect(() => {
    const loadEnquiries = async () => {
      const enquiries = await api.getEnquiriesByMonitor(
        0,
        1,
        monitor,
        "finished"
      );
      if (enquiries.items.length) {
        setEnquiry(enquiries.items[0]);
      }
    };

    loadEnquiries();
  }, [api, monitor]);

  if (enquiry && enquiry.monitor_type === "website_status_monitor") {
    return (
      <React.Fragment key={`${monitor.id}-last-completed-enquiry`}>
        <br />
        <WebsiteStatusResult enquiry={enquiry} margin={0} />
      </React.Fragment>
    );
  }

  return (
    <React.Fragment key={`${monitor.id}-last-completed-enquiry`}>
      {/* There is no result for this monitor yet. Let's just be silent here. */}
      <br />
    </React.Fragment>
  );
}
